// Pure, dependency-free CSV parser for utility meter data uploads.
// Spec: docs/plans/2026-07-20-001-feat-utility-benchmarking-multi-value-import-plan.md
// ("CSV parsing (ecomm-ui, extracted pure module)" section).
//
// Deliberately strict: files are hand-prepped clean per-meter CSVs. Quoted
// fields and thousands separators are rejected rather than parsed — if raw
// utility-company exports ever need support, this module is what changes.

export const ROW_CAP = 100000;

const METER_CONFIGS = {
    electric: {
        columnCount: 3,
        header: ['timestamp', 'kwh', 'kvarh'],
        headerLabel: 'timestamp,kwh,kvarh',
    },
    gas: {
        columnCount: 2,
        header: ['timestamp', 'mcf'],
        headerLabel: 'timestamp,mcf',
    },
};

// Meter types this parser (and the upload flow) supports.
export const SUPPORTED_METER_TYPES = Object.keys(METER_CONFIGS);

export const unsupportedMeterTypeError = meterType =>
    `CSV upload is not supported for meter type "${meterType}". Only electric and gas meters can accept data uploads.`;

const TIMESTAMP_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/;
const NUMBER_RE = /^-?(\d+(\.\d+)?|\.\d+)$/;

const QUOTED_FIELDS_ERROR =
    'The file appears to contain quoted fields or thousands separators (e.g. "1,234.5"), which are not supported. ' +
    'Save the CSV with plain unquoted values and re-upload.';

// M/D/YYYY H:mm (also MM/DD/YYYY and HH:mm). 4-digit year required; bounds
// validated. Output is naive ISO local, matching the server contract:
// YYYY-MM-DDTHH:mm:00
export const parseTimestamp = raw => {
    const match = TIMESTAMP_RE.exec(raw.trim());
    if (!match) return null;
    const [, m, d, y, h, min] = match;
    const month = Number(m);
    const day = Number(d);
    const hour = Number(h);
    const minute = Number(min);
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    if (hour > 23) return null;
    if (minute > 59) return null;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}T${h.padStart(2, '0')}:${min}:00`;
};

// Plain decimal with optional leading minus. Returns null for anything else
// (empty, exponent notation, thousands separators, stray text).
const parseNumber = raw => {
    const trimmed = raw.trim();
    if (!NUMBER_RE.test(trimmed)) return null;
    return parseFloat(trimmed);
};

const validateHeader = (headerLine, config, meterType) => {
    if (headerLine.includes('"')) {
        return QUOTED_FIELDS_ERROR;
    }
    const fields = headerLine.split(',').map(field => field.trim().toLowerCase());
    const matches =
        fields.length === config.header.length &&
        fields.every((field, index) => field === config.header[index]);
    if (!matches) {
        return `Invalid header for a ${meterType} meter: expected "${config.headerLabel}" but found "${headerLine.trim()}".`;
    }
    return null;
};

// Parses one electric data row's value fields. Returns the values array, or
// null when the row must be skipped (invalid/zero/negative kwh, invalid/
// negative kvarh). An EMPTY kvarh field is a valid partial row (KWH only).
const parseElectricValues = fields => {
    const kwh = parseNumber(fields[1]);
    if (kwh === null || kwh <= 0) return null;
    if (fields[2].trim() === '') {
        return [{ unit: 'KWH', value: kwh }];
    }
    const kvarh = parseNumber(fields[2]);
    if (kvarh === null || kvarh < 0) return null;
    return [
        { unit: 'KWH', value: kwh },
        { unit: 'KVARH', value: kvarh },
    ];
};

const parseGasValues = fields => {
    const mcf = parseNumber(fields[1]);
    if (mcf === null || mcf <= 0) return null;
    return [{ unit: 'MCF', value: mcf }];
};

// Parses a utility meter CSV for upload.
//
// Returns { ok: true, rows: [{timestamp, values: [{unit, value}]}], skipped, kwhOnly }
// or      { ok: false, error }
//
// - skipped counts data rows dropped by validation (bad timestamp/number,
//   zero/negative kwh or mcf, negative kvarh, quoted fields, wrong column count).
// - kwhOnly is true when at least one electric row parsed and none carried a
//   kvarh value (drives the success-toast wording). Always false for gas.
// - Duplicate timestamps are NOT deduped client-side — the server counts them.
export const parseUtilityMeterCsv = (fileText, meterType) => {
    const normalizedType = typeof meterType === 'string' ? meterType.toLowerCase() : '';
    const config = METER_CONFIGS[normalizedType];
    if (!config) {
        return { ok: false, error: unsupportedMeterTypeError(meterType) };
    }
    if (typeof fileText !== 'string') {
        return { ok: false, error: 'The file could not be read as text.' };
    }

    // Strip UTF-8 BOM and carriage returns, split lines, drop trailing blanks.
    let text = fileText;
    if (text.charCodeAt(0) === 0xfeff) {
        text = text.slice(1);
    }
    const lines = text.replace(/\r/g, '').split('\n');
    while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
        lines.pop();
    }
    if (lines.length === 0) {
        return { ok: false, error: 'The file is empty.' };
    }

    // Line-1 sniffing: a line whose first field is a valid timestamp is data
    // (headerless file); anything else is treated as a header and validated.
    const firstField = lines[0].split(',')[0];
    const hasHeader = parseTimestamp(firstField) === null;
    if (hasHeader) {
        const headerError = validateHeader(lines[0], config, normalizedType);
        if (headerError) {
            return { ok: false, error: headerError };
        }
    }

    // Row cap — checked before any per-row parsing.
    const dataLines = hasHeader ? lines.slice(1) : lines;
    if (dataLines.length > ROW_CAP) {
        return {
            ok: false,
            error: `The file contains ${dataLines.length} data rows, which exceeds the maximum of ${ROW_CAP}. Split the file and upload it in parts.`,
        };
    }

    const rows = [];
    let skipped = 0;
    let dataRowCount = 0;
    let quotedRowCount = 0;
    let badColumnRowCount = 0;
    let sawKvarh = false;

    for (const line of dataLines) {
        if (line.trim() === '') continue; // interior blank lines are ignored, not counted
        dataRowCount += 1;

        // Quoted fields are unsupported; thousands separators surface as extra
        // comma-split fields and fail the exact column count below.
        if (line.includes('"')) {
            quotedRowCount += 1;
            skipped += 1;
            continue;
        }
        const fields = line.split(',');
        if (fields.length !== config.columnCount) {
            badColumnRowCount += 1;
            skipped += 1;
            continue;
        }

        const timestamp = parseTimestamp(fields[0]);
        if (timestamp === null) {
            skipped += 1;
            continue;
        }

        const values =
            normalizedType === 'electric' ? parseElectricValues(fields) : parseGasValues(fields);
        if (values === null) {
            skipped += 1;
            continue;
        }
        if (values.some(entry => entry.unit === 'KVARH')) {
            sawKvarh = true;
        }
        rows.push({ timestamp, values });
    }

    if (dataRowCount === 0) {
        return { ok: false, error: 'No data rows found in the file.' };
    }
    // Majority-quoted files are a format problem, not a bad-rows problem.
    if (quotedRowCount * 2 > dataRowCount) {
        return { ok: false, error: QUOTED_FIELDS_ERROR };
    }
    if (rows.length === 0) {
        if (badColumnRowCount * 2 > dataRowCount) {
            return {
                ok: false,
                error: `Every row was invalid: rows do not have the ${config.columnCount} columns expected for a ${normalizedType} meter (${config.headerLabel}). The file may be for a different meter type.`,
            };
        }
        return {
            ok: false,
            error: `No valid data rows found — all ${dataRowCount} rows were skipped (invalid timestamps or values).`,
        };
    }

    const kwhOnly = normalizedType === 'electric' && !sawKvarh;
    return { ok: true, rows, skipped, kwhOnly };
};
